import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { Preview } from './Preview'
import type { RendererFor } from './renderers'

/**
 * A marked-up text on paper.
 *
 * The screen and the page are not the same thing, and the editor is built for the screen: panes as
 * tall as the window with the text scrolling inside them. Printing that prints one screenful with a
 * scrollbar drawn down the side of it — which is what MyLib did, and what this exists to stop.
 *
 * So printing does not print the screen. It draws the text **once more, whole**, outside the
 * application's layout altogether, and prints that. Three things follow from it:
 *
 * - **The rendering is waited for.** A renderer is asked for asynchronously and Asciidoctor is fetched
 *   the first time an AsciiDoc text needs it, so the print dialog opens when the text is drawn and not
 *   a moment before it. That is what `onDrawn` on the preview is for.
 * - **What is printed is what is on the screen**, unsaved keystrokes included — the text is handed in
 *   rather than fetched again. A document saves itself as it is written, so anything else would print
 *   a version somebody has already moved on from.
 * - **It carries the same classes as the preview**, so the typography on paper is the typography on the
 *   screen. A print stylesheet that drew a second look would be a second design to keep in step.
 *
 * The page is put at the end of the body rather than where it was asked for: the print stylesheet then
 * hides the application in one rule instead of walking up the tree hiding everything beside it.
 */
export interface PrintOutProps {
  readonly text: string
  /** Which markup this is, as a media type — the content's own. */
  readonly format: string
  /**
   * What is being printed.
   *
   * It becomes the document's title while the dialog is open, which is what the browser writes into
   * the header of the page and what "save as PDF" offers as the file name. A print called
   * `MyLib` would be one nobody can find again.
   */
  readonly title: string
  /** Said when the dialog is gone, so whoever asked can take this off the page again. */
  readonly onDone: () => void
  /** For a test, or for the day a renderer comes from somewhere else. */
  readonly rendererFor?: RendererFor
}

export function PrintOut({ text, format, title, onDone, rendererFor }: PrintOutProps) {
  const [drawn, setDrawn] = useState(false)

  /*
   * The application is not printed while this is on the page, and the page is called what is being
   * printed. Both are put back when this goes, whether the dialog was used or dismissed.
   */
  useEffect(() => {
    const called = document.title
    document.title = title
    document.body.classList.add('is-printing')
    return () => {
      document.title = called
      document.body.classList.remove('is-printing')
    }
  }, [title])

  /*
   * The way back when nothing is ever drawn.
   *
   * A rendering is fetched over the network the first time a markup needs it, and a fetch that never
   * answers would otherwise leave the control saying "printing" for the rest of the session. Giving
   * up says nothing and prints nothing — it simply hands the control back, so pressing it again is
   * possible.
   */
  useEffect(() => {
    if (drawn) {
      return
    }
    const giveUp = setTimeout(onDone, NOTHING_IS_COMING_MS)
    return () => {
      clearTimeout(giveUp)
    }
  }, [drawn, onDone])

  useEffect(() => {
    if (!drawn) {
      return
    }

    let finished = false
    const finish = () => {
      if (!finished) {
        finished = true
        onDone()
      }
    }

    /*
     * A frame later, so that what was just drawn has been laid out. `afterprint` is what Safari says
     * when the dialog closes; Chrome and Firefox simply return from `print()`. Either is an answer,
     * and whichever comes first is the one that counts.
     */
    window.addEventListener('afterprint', finish)
    const frame = requestAnimationFrame(() => {
      try {
        window.print()
      } finally {
        finish()
      }
    })

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('afterprint', finish)
    }
  }, [drawn, onDone])

  return createPortal(
    /*
     * Hidden from a screen reader, and by the stylesheet from the screen: this is the same text a
     * second time, and a second copy of a document in the reading order is a document read twice.
     */
    <div className="printout" aria-hidden="true">
      <Preview
        text={text}
        format={format}
        label={title}
        rendererFor={rendererFor}
        onDrawn={() => {
          setDrawn(true)
        }}
      />
    </div>,
    document.body,
  )
}

/**
 * How long a drawing may take before printing is given up on.
 *
 * Generous: this is a megabyte of renderer over a lecture-hall connection on a slow first use, and
 * the only cost of waiting is a control that says it is busy.
 */
const NOTHING_IS_COMING_MS = 20_000
